import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Plus, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth, API } from "../App";
import { extractError } from "../lib/errors";
import { toast } from "sonner";

const StepCard = ({ step, stepData, principleColor, onSave, onSuggest }) => {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    principle_id: step.id,
    ccp_name: stepData?.ccp_name || "",
    hazard_description: stepData?.hazard_description || "",
    critical_limit: stepData?.critical_limit || "",
    monitoring_procedure: stepData?.monitoring_procedure || "",
    corrective_action: stepData?.corrective_action || "",
    verification_method: stepData?.verification_method || "",
    records: stepData?.records || "",
  });
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const isComplete = stepData && stepData.ccp_name;

  const handleSave = async () => {
    setSaving(true);
    await onSave(step.id, form);
    setSaving(false);
  };

  const handleSuggest = async () => {
    setAiLoading(true);
    const text = await onSuggest(step);
    if (text) {
      setForm((prev) => ({
        ...prev,
        hazard_description: prev.hazard_description
          ? `${prev.hazard_description}\n\n${text}`
          : text,
      }));
    }
    setAiLoading(false);
  };

  return (
    <div className="border border-border rounded-lg">
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-[10px] font-mono flex-shrink-0">{step.step}</Badge>
            <span className="text-sm font-medium truncate">{step.title}</span>
            {isComplete && (
              <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 flex-shrink-0">
                <CheckCircle2 className="w-2.5 h-2.5 mr-1" /> Documentado
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{step.description}</p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">{step.description}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nombre del CCP / Medida</Label>
              <Input className="h-8 text-sm mt-1" placeholder="Ej: CCP-1 Cocción"
                value={form.ccp_name} onChange={(e) => setForm({ ...form, ccp_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Límite crítico</Label>
              <Input className="h-8 text-sm mt-1" placeholder="Ej: T ≥ 72°C por 15 seg"
                value={form.critical_limit} onChange={(e) => setForm({ ...form, critical_limit: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Descripción del peligro</Label>
            <Textarea className="text-sm h-16 resize-none mt-1" placeholder="Tipo de peligro (B/Q/F) y justificación de significancia..."
              value={form.hazard_description} onChange={(e) => setForm({ ...form, hazard_description: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Procedimiento de monitoreo</Label>
              <Textarea className="text-sm h-16 resize-none mt-1" placeholder="Qué, cómo, frecuencia, responsable..."
                value={form.monitoring_procedure} onChange={(e) => setForm({ ...form, monitoring_procedure: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Acción correctiva</Label>
              <Textarea className="text-sm h-16 resize-none mt-1" placeholder="Acción ante desviación del LC..."
                value={form.corrective_action} onChange={(e) => setForm({ ...form, corrective_action: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Método de verificación</Label>
              <Input className="h-8 text-sm mt-1" placeholder="Ej: Revisión semanal de registros"
                value={form.verification_method} onChange={(e) => setForm({ ...form, verification_method: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Registros requeridos</Label>
              <Input className="h-8 text-sm mt-1" placeholder="Ej: Registro de temperaturas diario"
                value={form.records} onChange={(e) => setForm({ ...form, records: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving || !form.ccp_name}>
              {saving ? "Guardando..." : "Guardar paso"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleSuggest} disabled={aiLoading}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {aiLoading ? "Generando..." : "Sugerir con AI"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const PRINCIPLE_COLORS = ["#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#10b981", "#f97316", "#06b6d4"];

const ZonaFrancaHACCP = () => {
  const { token } = useAuth();
  const [principles, setPrinciples] = useState([]);
  const [plan, setPlan] = useState({});
  const [loading, setLoading] = useState(true);
  const [openPrinciples, setOpenPrinciples] = useState({ "haccp-p1": true });

  const load = useCallback(async () => {
    try {
      const [ctrlRes, planRes] = await Promise.all([
        axios.get(`${API}/zona-franca/controls/haccp`),
        axios.get(`${API}/zona-franca/haccp/plan`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPrinciples(ctrlRes.data.principles || []);
      setPlan(planRes.data.steps || {});
    } catch {
      toast.error("No se pudo cargar el plan HACCP");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSaveStep = async (stepId, body) => {
    try {
      await axios.put(`${API}/zona-franca/haccp/plan/step/${stepId}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlan((prev) => ({ ...prev, [stepId]: body }));
      toast.success("Paso HACCP guardado");
    } catch {
      toast.error("Error al guardar el paso");
    }
  };

  const handleSuggestStep = async (step) => {
    try {
      const res = await axios.post(
        `${API}/zona-franca/ai/suggest`,
        {
          framework_code: "haccp",
          control_id: step.id,
          control_title: `${step.step} — ${step.title}`,
          control_description: step.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sugerencia generada");
      return res.data.suggestion;
    } catch (err) {
      toast.error(extractError(err, "No se pudo generar sugerencia"));
      return "";
    }
  };

  const allSteps = principles.flatMap((p) => p.steps || []);
  const documented = allSteps.filter((s) => plan[s.id]?.ccp_name).length;
  const total = allSteps.length;
  const pct = total ? Math.round((documented / total) * 100) : 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#ef444420", border: "1px solid #ef444440" }}>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">HACCP — Plan de Análisis de Peligros</h1>
            <p className="text-sm text-muted-foreground">7 principios · {total} pasos de implementación · Codex Alimentarius</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">Codex CAC/RCP 1-1969</Badge>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Pasos documentados", value: `${documented}/${total}` },
          { label: "Progreso del plan", value: `${pct}%` },
          { label: "Principios cubiertos", value: `${principles.filter((p) => p.steps?.some((s) => plan[s.id]?.ccp_name)).length}/${principles.length}` },
        ].map((k) => (
          <Card key={k.label}><CardContent className="p-4">
            <p className="text-2xl font-bold text-red-500">{loading ? "—" : k.value}</p>
            <p className="text-xs text-muted-foreground">{k.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card><CardContent className="p-4">
        <div className="flex justify-between text-sm mb-1">
          <span>Completitud del Plan HACCP</span>
          <span className="font-medium text-red-500">{pct}%</span>
        </div>
        <Progress value={pct} className="h-2" />
      </CardContent></Card>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {principles.map((principle, idx) => {
            const color = PRINCIPLE_COLORS[idx % PRINCIPLE_COLORS.length];
            const isOpen = openPrinciples[principle.id] ?? false;
            const stepsDocumented = (principle.steps || []).filter((s) => plan[s.id]?.ccp_name).length;

            return (
              <Card key={principle.id} className="overflow-hidden">
                <CardHeader className="pb-0" style={{ borderLeft: `3px solid ${color}` }}>
                  <button
                    className="w-full flex items-center justify-between text-left"
                    onClick={() => setOpenPrinciples((p) => ({ ...p, [principle.id]: !p[principle.id] }))}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                        style={{ backgroundColor: color }}>
                        {principle.number}
                      </div>
                      <div>
                        <CardTitle className="text-sm">{principle.title}</CardTitle>
                        <CardDescription className="text-xs">
                          {stepsDocumented}/{principle.steps?.length || 0} pasos documentados
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {stepsDocumented === (principle.steps?.length || 0) && stepsDocumented > 0 && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      )}
                      {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>
                </CardHeader>
                {isOpen && (
                  <CardContent className="pt-3 space-y-2">
                    <p className="text-sm text-muted-foreground">{principle.description}</p>
                    {(principle.steps || []).map((step) => (
                      <StepCard
                        key={step.id}
                        step={step}
                        stepData={plan[step.id]}
                        principleColor={color}
                        onSave={handleSaveStep}
                        onSuggest={handleSuggestStep}
                      />
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZonaFrancaHACCP;
